import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Menu as MantineMenu, Overlay } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconMenu2, IconX } from '@tabler/icons-react'
import clsx from 'clsx'
import { useDispatch, useSelector } from 'common/hooks'
import { IMenuItem } from 'common/models'
import { hasProperty } from 'common/utils'
import { closeMobileMenu, openMobileMenu } from 'iframe/store'

/**
 * Menu mobilne - hamburger w headerze.
 * Widoczny tylko na urządzeniach mobilnych (< 768px).
 */
export default function MobileMenu() {
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const { menu, isMobileMenuOpen } = useSelector((state) => state.iframe)
  const [expandedFloatingMenuItem, setExpandedFloatingMenuItem] = useState<string | null>(
    null,
  )

  // Funkcja pomocnicza do sprawdzania czy link jest aktywny
  const isLinkActive = useCallback(
    (link: string): boolean => {
      const currentPath = location.pathname.replace(/\?.*$/, '')
      const normalizedLink = link.replace(/\?.*$/, '')

      // Specjalny przypadek dla root path '/'
      if (normalizedLink === '/') {
        return currentPath === '/'
      }

      // Dokładne dopasowanie
      return currentPath === normalizedLink
    },
    [location.pathname],
  )

  // Funkcja pomocnicza do sprawdzania czy sublink jest aktywny
  const isSubLinkActive = useCallback(
    (parentLink: string, subLink: string): boolean => {
      const currentPath = location.pathname.replace(/\?.*$/, '')
      const fullSubLinkPath = `${parentLink}/${subLink}`.replace(/\/+/g, '/') // Usuwa podwójne slashe

      // Sprawdź dokładne dopasowanie lub czy pathname zaczyna się od pełnej ścieżki sublinka
      return (
        currentPath === fullSubLinkPath || currentPath.startsWith(`${fullSubLinkPath}/`)
      )
    },
    [location.pathname],
  )

  // Funkcja pomocnicza do sprawdzania czy którykolwiek sublink jest aktywny
  const hasActiveSubLink = useCallback(
    (parentLink: string, childs?: IMenuItem[]): boolean => {
      if (!childs || childs.length === 0) return false
      return childs.some((sub) => isSubLinkActive(parentLink, sub.link))
    },
    [isSubLinkActive],
  )

  const handleMenuChange = (opened: boolean) => {
    if (opened) {
      dispatch(openMobileMenu())
    } else {
      dispatch(closeMobileMenu())
      // Reset accordion gdy menu się zamyka
      setExpandedFloatingMenuItem(null)
    }
  }

  // Zamykaj submenu za każdym razem, gdy zamykamy menu główne
  useEffect(() => {
    if (!isMobileMenuOpen) {
      setExpandedFloatingMenuItem(null)
    }
  }, [isMobileMenuOpen])

  if (!menu || menu.length === 0) return null

  return (
    <>
      {isMobileMenuOpen && (
        <Overlay
          backgroundOpacity={0.15}
          blur={1}
          className={'top-[60px]'}
          color={'#000'}
        />
      )}
      <div className={'flex items-center flex-shrink-0 md:hidden'}>
        <MantineMenu
          classNames={{
            dropdown:
              'flex flex-col gap-1 !w-[calc(100vw-16px)] p-4 !max-h-[calc(100vh-68px)] overflow-y-auto !left-2',
          }}
          onChange={handleMenuChange}
          opened={isMobileMenuOpen}
          position={'bottom-start'}
        >
          <MantineMenu.Target>
            <button
              className={
                'bg-transparent border-0 cursor-pointer flex items-center justify-center text-white hover:bg-white/10 rounded transition-colors duration-150'
              }
            >
              {isMobileMenuOpen ? (
                <IconX size={24} stroke={2} />
              ) : (
                <IconMenu2 size={24} stroke={2} />
              )}
            </button>
          </MantineMenu.Target>
          <MantineMenu.Dropdown>
            {menu.map((item, index) => {
              if (hasProperty(item, 'childs')) {
                const isExpanded = expandedFloatingMenuItem === item.link
                const isSubLinkActiveForParent = hasActiveSubLink(item.link, item.childs)

                return (
                  <div className={index > 0 ? 'mt-1' : ''} key={item.link}>
                    <button
                      className={clsx(
                        'bg-transparent cursor-pointer border-0 w-full flex items-center justify-between px-0 font-medium text-lg text-black hover:bg-gray-100 rounded transition-colors duration-150',
                        isExpanded && 'bg-gray-50',
                        isSubLinkActiveForParent &&
                          '!underline !underline-offset-4 decoration-2',
                      )}
                      onClick={() => {
                        // Accordion behavior - tylko jeden element otwarty naraz
                        setExpandedFloatingMenuItem(isExpanded ? null : item.link)
                      }}
                    >
                      <span className={'flex-1 text-left'}>{t(item.label)}</span>
                      <div className={'ml-2 flex-shrink-0 text-gray-500'}>
                        {isExpanded ? (
                          <IconChevronUp size={16} stroke={2} />
                        ) : (
                          <IconChevronDown size={16} stroke={2} />
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <div
                        className={'flex flex-col border-l-2 border-gray-200 ml-3 my-1'}
                      >
                        {item.childs?.map((sub) => {
                          const isSubActive = isSubLinkActive(item.link, sub.link)
                          return (
                            <Link
                              className={clsx(
                                'py-1 no-underline font-normal text-lg text-gray-700 whitespace-nowrap hover:bg-gray-100 rounded transition-colors duration-150',
                                isSubActive &&
                                  '!text-black !font-medium bg-gray-50 !underline !underline-offset-4 decoration-2',
                              )}
                              key={`${item.link}-${sub.link}-${sub.label}`}
                              onClick={() => {
                                setExpandedFloatingMenuItem(null)
                                dispatch(closeMobileMenu())
                              }}
                              to={`${item.link}/${sub.link}`}
                            >
                              {t(sub.label)}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              }

              const isActive = isLinkActive(item.link)

              return (
                <Link
                  className={clsx(
                    'block no-underline font-medium text-lg text-black hover:bg-gray-100 rounded transition-colors duration-150',
                    isActive && '!underline !underline-offset-4 decoration-2',
                    index > 0 && 'mt-1',
                  )}
                  key={item.link}
                  onClick={() => dispatch(closeMobileMenu())}
                  to={item.link}
                >
                  {t(item.label)}
                </Link>
              )
            })}
          </MantineMenu.Dropdown>
        </MantineMenu>
      </div>
    </>
  )
}
